import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDownload } from '@/context/AppDownloadContext';
import {
  dailyMissions,
  weeklyMissions,
  monthlyMissions,
  rewardsCatalog,
  unifiedProfile,
  gamificationGlobalStats,
  getMissionsByPeriod,
  getMissionProgress,
  getTotalDailyPoints,
  missionPeriodLabels,
  rewardTypeLabels,
  type GamificationMission,
} from '@/mocks/rfwGamification';
import { listenerLevels } from '@/mocks/listenerLevels';
import { badgeCollection } from '@/mocks/listenerBadges';

type MissionPeriod = 'daily' | 'weekly' | 'monthly';

export default function GamificacionTotalSection() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [activePeriod, setActivePeriod] = useState<MissionPeriod>('daily');
  const [expandedMission, setExpandedMission] = useState<string | null>(null);

  const profile = unifiedProfile;
  const stats = gamificationGlobalStats;
  const currentMissions = getMissionsByPeriod(activePeriod);
  const missionProgress = getMissionProgress(currentMissions);
  const earnedBadges = badgeCollection.filter((b) => b.earned);

  const periodTabs: { key: MissionPeriod; label: string; icon: string; color: string; bg: string }[] = [
    { key: 'daily', label: 'Hoy', icon: 'ri-sun-line', color: 'text-amber-500', bg: 'bg-amber-500' },
    { key: 'weekly', label: 'Semana', icon: 'ri-calendar-line', color: 'text-emerald-500', bg: 'bg-emerald-500' },
    { key: 'monthly', label: 'Mes', icon: 'ri-calendar-2-line', color: 'text-rose-500', bg: 'bg-rose-500' },
  ];

  const toggleMissionExpand = (id: string) => {
    setExpandedMission(expandedMission === id ? null : id);
  };

  return (
    <section id="gamificacion-total" className="relative py-20 md:py-28 overflow-hidden bg-background-100">
      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-500/4 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/3 left-0 w-[400px] h-[400px] bg-emerald-500/3 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-[450px] h-[350px] bg-rose-500/3 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* ═══════════ HEADER ═══════════ */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold mb-5">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></div>
            Gamificación Total
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Volver cada día{' '}
            <span className="text-amber-600">vale la pena</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Misiones diarias, insignias, niveles y recompensas. Cada acción te acerca más a ser Embajador RFW. La comunidad te reconoce por tu dedicación.
          </p>

          {/* Global stats bar */}
          <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-3 px-4 py-3 bg-background-50 rounded-2xl">
            {[
              { value: `${(stats.totalListenersGamified / 1000).toFixed(1)}K`, label: 'Oyentes compitiendo', icon: 'ri-group-line', color: 'text-primary-500' },
              { value: `${(stats.missionsCompletedToday / 1000).toFixed(1)}K`, label: 'Misiones completadas hoy', icon: 'ri-check-double-line', color: 'text-emerald-500' },
              { value: `${(stats.totalPointsAwardedToday / 1000).toFixed(1)}K`, label: 'Puntos otorgados hoy', icon: 'ri-flashlight-line', color: 'text-amber-500' },
              { value: `${(stats.activeStreaks / 1000).toFixed(1)}K`, label: 'Rachas activas', icon: 'ri-fire-line', color: 'text-orange-500' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-2 px-3 py-1.5">
                <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                <span className="text-sm font-bold text-foreground-950">{stat.value}</span>
                <span className="text-[10px] text-foreground-500 whitespace-nowrap">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════ 2-COLUMN LAYOUT: Misiones (2/3) + Perfil (1/3) ═══════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">

          {/* ── LEFT: MISIONES ACTIVAS (2 cols) ── */}
          <div className="lg:col-span-2">
            <div className="bg-background-50 rounded-2xl overflow-hidden">
              {/* Tab header */}
              <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                  <i className="ri-treasure-map-line text-amber-500"></i>
                  Misiones Activas
                </h3>
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                  {periodTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActivePeriod(tab.key)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        activePeriod === tab.key
                          ? `${tab.bg} text-background-50 shadow-sm`
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      <i className={`${tab.icon} text-xs`}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Progress bar */}
              <div className="px-5 pb-1">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-foreground-500">
                    {missionProgress.completed}/{missionProgress.total} completadas
                  </span>
                  <span className="text-xs font-bold text-foreground-700">
                    +{missionProgress.earnedPoints} / {missionProgress.totalPoints} pts
                  </span>
                </div>
                <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ease-out ${
                      activePeriod === 'daily' ? 'bg-amber-500' :
                      activePeriod === 'weekly' ? 'bg-emerald-500' : 'bg-rose-500'
                    }`}
                    style={{ width: `${missionProgress.percent}%` }}
                  ></div>
                </div>
              </div>

              {/* Mission cards */}
              <div className="px-5 pb-5 pt-3 space-y-3">
                {currentMissions.map((mission) => {
                  const pct = Math.round((mission.progress / mission.required) * 100);
                  const colorMap: Record<string, { bg: string; border: string; bar: string; iconBg: string; iconText: string }> = {
                    primary: { bg: 'bg-primary-50', border: 'border-primary-200', bar: 'bg-primary-500', iconBg: 'bg-primary-500', iconText: 'text-background-50' },
                    accent: { bg: 'bg-accent-50', border: 'border-accent-200', bar: 'bg-accent-500', iconBg: 'bg-accent-500', iconText: 'text-background-50' },
                    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', bar: 'bg-emerald-500', iconBg: 'bg-emerald-500', iconText: 'text-background-50' },
                    sky: { bg: 'bg-sky-50', border: 'border-sky-200', bar: 'bg-sky-500', iconBg: 'bg-sky-500', iconText: 'text-background-50' },
                    rose: { bg: 'bg-rose-50', border: 'border-rose-200', bar: 'bg-rose-500', iconBg: 'bg-rose-500', iconText: 'text-background-50' },
                    amber: { bg: 'bg-amber-50', border: 'border-amber-200', bar: 'bg-amber-500', iconBg: 'bg-amber-500', iconText: 'text-background-50' },
                    orange: { bg: 'bg-orange-50', border: 'border-orange-200', bar: 'bg-orange-500', iconBg: 'bg-orange-500', iconText: 'text-background-50' },
                    violet: { bg: 'bg-violet-50', border: 'border-violet-200', bar: 'bg-violet-500', iconBg: 'bg-violet-500', iconText: 'text-background-50' },
                  };
                  const c = colorMap[mission.color] || colorMap.primary;

                  return (
                    <div
                      key={mission.id}
                      className={`rounded-xl border p-4 transition-all cursor-pointer ${
                        mission.completed
                          ? 'bg-emerald-50/50 border-emerald-200'
                          : `${c.bg} ${c.border} hover:shadow-sm`
                      }`}
                      onClick={() => toggleMissionExpand(mission.id)}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          mission.completed ? 'bg-emerald-500' : c.iconBg
                        }`}>
                          <i className={`${mission.icon} ${mission.completed ? 'text-background-50' : c.iconText} text-lg`}></i>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h4 className={`text-sm font-bold ${mission.completed ? 'text-emerald-700' : 'text-foreground-950'}`}>
                                {mission.title}
                              </h4>
                              <p className="text-xs text-foreground-500 mt-0.5">{mission.description}</p>
                            </div>
                            <div className="shrink-0 text-right">
                              {mission.completed ? (
                                <div className="flex items-center gap-1 text-emerald-600">
                                  <i className="ri-checkbox-circle-fill text-sm"></i>
                                  <span className="text-xs font-bold">+{mission.points}</span>
                                </div>
                              ) : (
                                <span className="text-xs font-bold text-foreground-600">+{mission.points} pts</span>
                              )}
                            </div>
                          </div>

                          {/* Progress bar */}
                          {!mission.completed && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-foreground-500">
                                  {mission.progress} / {mission.required} {mission.unit}
                                </span>
                                <span className="text-[10px] font-bold text-foreground-600">{pct}%</span>
                              </div>
                              <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }}></div>
                              </div>
                            </div>
                          )}

                          {/* Expanded detail */}
                          {expandedMission === mission.id && (
                            <div className="mt-3 pt-3 border-t border-background-200/50">
                              <div className="flex items-center justify-between text-[10px]">
                                <span className="text-foreground-500">{mission.resetLabel}</span>
                                <span className={`font-semibold ${mission.completed ? 'text-emerald-600' : 'text-foreground-400'}`}>
                                  {mission.completed ? 'Completada' : 'En progreso'}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── RIGHT: PERFIL UNIFICADO (1 col) ── */}
          <div className="space-y-4">
            {/* Profile card */}
            <div className="bg-background-50 rounded-2xl overflow-hidden">
              <div className="relative h-20 bg-gradient-to-br from-orange-400/30 via-amber-400/20 to-rose-400/10">
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2">
                  <img
                    src={profile.photoUrl}
                    alt={profile.name}
                    className="w-14 h-14 rounded-2xl object-cover object-top border-3 border-background-50"
                  />
                </div>
              </div>
              <div className="pt-8 pb-4 px-4 text-center">
                <h4 className="text-sm font-bold text-foreground-950">{profile.name}</h4>
                <p className="text-xs text-foreground-500">{profile.username}</p>

                {/* Level badge */}
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 rounded-xl">
                  <i className="ri-fire-line text-orange-600 text-sm"></i>
                  <span className="text-xs font-bold text-orange-700">{profile.currentLevelName}</span>
                </div>

                {/* Progress to next level */}
                <div className="mt-3 px-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-foreground-500">Nivel {profile.currentLevel}</span>
                    <span className="text-[10px] font-bold text-foreground-700">{profile.progressPercent}%</span>
                  </div>
                  <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-400 to-amber-500 rounded-full" style={{ width: `${profile.progressPercent}%` }}></div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-foreground-400">{profile.totalPoints.toLocaleString()} pts</span>
                    <span className="text-[10px] text-foreground-400">{profile.pointsToNextLevel.toLocaleString()} pts</span>
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-background-200">
                  {[
                    { value: profile.globalRank.toLocaleString(), label: 'Rank Global', icon: 'ri-medal-line', color: 'text-amber-500' },
                    { value: `${profile.badgesEarned}/${profile.badgesTotal}`, label: 'Insignias', icon: 'ri-award-line', color: 'text-emerald-500' },
                    { value: `${profile.streakDays}`, label: 'Días Racha', icon: 'ri-fire-line', color: 'text-orange-500' },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <i className={`${s.icon} ${s.color} text-xs`}></i>
                      <div className="text-sm font-extrabold text-foreground-950 mt-0.5">{s.value}</div>
                      <div className="text-[10px] text-foreground-500">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Streak + Today */}
            <div className="bg-background-50 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-foreground-700 flex items-center gap-1.5">
                  <i className="ri-sun-line text-amber-500"></i>
                  Hoy
                </span>
                <span className="text-xs font-bold text-amber-600">+{profile.todayPoints} pts</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-foreground-700 flex items-center gap-1.5">
                  <i className="ri-calendar-line text-emerald-500"></i>
                  Esta Semana
                </span>
                <span className="text-xs font-bold text-emerald-600">{profile.weeklyPoints}/{profile.weeklyGoal} pts</span>
              </div>
              <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(profile.weeklyPoints / profile.weeklyGoal) * 100}%` }}></div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="space-y-2">
              <Link
                to="/gamificacion"
                className="w-full flex items-center justify-between px-4 py-3 bg-background-50 rounded-xl hover:shadow-sm transition-all cursor-pointer group"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
                  <i className="ri-flashlight-line text-primary-600"></i>
                  Mi Gamificación
                </span>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors text-sm"></i>
              </Link>
              <Link
                to="/insignias"
                className="w-full flex items-center justify-between px-4 py-3 bg-background-50 rounded-xl hover:shadow-sm transition-all cursor-pointer group"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
                  <i className="ri-award-line text-emerald-600"></i>
                  Mis Insignias
                </span>
                <span className="text-xs text-emerald-600 font-bold">{profile.badgesEarned}/{profile.badgesTotal}</span>
              </Link>
              <Link
                to="/niveles"
                className="w-full flex items-center justify-between px-4 py-3 bg-background-50 rounded-xl hover:shadow-sm transition-all cursor-pointer group"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
                  <i className="ri-stack-line text-orange-600"></i>
                  Niveles
                </span>
                <span className="text-xs text-orange-600 font-bold">Nivel {profile.currentLevel}</span>
              </Link>
            </div>
          </div>
        </div>

        {/* ═══════════ LEVEL PROGRESSION STRIP ═══════════ */}
        <div className="mb-10 max-w-4xl mx-auto">
          <p className="text-center text-foreground-400 text-xs font-semibold uppercase tracking-wider mb-5">Tu camino de oyente</p>
          <div className="flex items-center justify-between gap-1">
            {listenerLevels.map((level, idx, arr) => {
              const reached = profile.currentLevel >= level.level;
              const isCurrent = profile.currentLevel === level.level;
              return (
                <div key={level.name} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1.5">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                      isCurrent
                        ? 'bg-orange-500 text-background-50 shadow-md shadow-orange-500/30 scale-110'
                        : reached
                        ? 'bg-accent-500 text-background-50 shadow-sm'
                        : 'bg-foreground-200/50 text-foreground-400'
                    }`}>
                      <i className={`${level.icon} text-sm`}></i>
                    </div>
                    <span className={`text-[10px] font-semibold whitespace-nowrap ${reached ? 'text-foreground-900' : 'text-foreground-400'}`}>
                      {level.name}
                    </span>
                    <span className="text-foreground-400 text-[9px] whitespace-nowrap">{level.pointsRequired.toLocaleString()} pts</span>
                  </div>
                  {idx < arr.length - 1 && (
                    <div className="flex-1 h-0.5 mx-1 rounded-full bg-foreground-200/50 relative overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ${
                          reached ? 'bg-accent-400 w-full' : 'bg-transparent w-0'
                        }`}
                      ></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════════ BADGES HIGHLIGHT ═══════════ */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
              <i className="ri-award-line text-emerald-500"></i>
              Insignias Destacadas
            </h3>
            <Link to="/insignias" className="text-xs text-emerald-600 font-medium hover:text-emerald-700 transition-colors cursor-pointer flex items-center gap-1">
              Ver todas <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {badgeCollection.map((badge) => {
              const isEarned = badge.earned;
              return (
                <div
                  key={badge.id}
                  className={`bg-background-50 rounded-2xl p-4 text-center transition-all group ${
                    isEarned ? 'hover:shadow-sm' : 'opacity-60 hover:opacity-75'
                  }`}
                >
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center ${
                    isEarned ? badge.color : 'bg-foreground-300'
                  }`}>
                    <i className={`${badge.icon} ${isEarned ? 'text-background-50' : 'text-background-100'} text-lg`}></i>
                  </div>
                  <h4 className={`text-xs font-bold mb-0.5 ${isEarned ? 'text-foreground-950' : 'text-foreground-500'}`}>
                    {badge.name}
                  </h4>
                  {isEarned ? (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold">
                      Desbloqueada
                    </span>
                  ) : (
                    <div className="mt-1">
                      <div className="w-full h-1 bg-background-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${badge.color}`} style={{ width: `${badge.progress}%` }}></div>
                      </div>
                      <span className="text-[10px] text-foreground-400">{badge.progress}%</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════════ REWARDS CATALOG ═══════════ */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
              <i className="ri-gift-line text-rose-500"></i>
              Catálogo de Recompensas
            </h3>
            <span className="text-xs text-foreground-500">{rewardsCatalog.filter((r) => r.unlocked).length}/{rewardsCatalog.length} desbloqueadas</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {rewardsCatalog.map((reward) => {
              const typeInfo = rewardTypeLabels[reward.type];
              return (
                <div
                  key={reward.id}
                  className={`bg-background-50 rounded-2xl p-4 transition-all group ${
                    reward.unlocked ? 'hover:shadow-sm' : 'opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                      reward.unlocked ? 'bg-emerald-100 text-emerald-700' : 'bg-background-200 text-foreground-500'
                    }`}>
                      <i className={`${typeInfo.icon} mr-0.5`}></i>
                      {typeInfo.label}
                    </span>
                  </div>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                    reward.unlocked ? reward.color : 'bg-foreground-200'
                  }`}>
                    <i className={`${reward.icon} ${reward.unlocked ? 'text-background-50' : 'text-foreground-400'} text-lg`}></i>
                  </div>
                  <h4 className={`text-sm font-bold mb-1 ${reward.unlocked ? 'text-foreground-950' : 'text-foreground-500'}`}>
                    {reward.name}
                  </h4>
                  <p className="text-xs text-foreground-500 leading-relaxed mb-2">{reward.description}</p>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className={`font-bold ${reward.unlocked ? 'text-foreground-700' : 'text-foreground-400'}`}>
                      {reward.value}
                    </span>
                    {reward.unlocked ? (
                      <span className="text-emerald-600 font-semibold">Desbloqueada</span>
                    ) : (
                      <span className="text-foreground-400">{reward.unlockCondition}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════════ CTA ═══════════ */}
        <div className="text-center bg-background-50 rounded-2xl p-8 md:p-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
            <i className="ri-rocket-line text-background-50 text-3xl"></i>
          </div>
          <h3 className="text-xl font-extrabold text-foreground-950 mb-2">
            Cada día es una nueva misión
          </h3>
          <p className="text-sm text-foreground-500 mb-5 max-w-lg mx-auto">
            Completá misiones, ganá insignias, subí de nivel. La comunidad RFW te espera. ¿Hasta dónde vas a llegar?
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={openDownloadModal}
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line mr-1.5"></i>
              Descargar App RFW
            </button>
            <Link
              to="/gamificacion"
              className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-flashlight-line mr-1.5"></i>
              Mi Gamificación
            </Link>
            <Link
              to="/insignias"
              className="px-5 py-2.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-award-line mr-1.5"></i>
              Insignias
            </Link>
            <Link
              to="/niveles"
              className="px-5 py-2.5 bg-orange-100 hover:bg-orange-200 text-orange-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-stack-line mr-1.5"></i>
              Niveles
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}